import type { ChannelId } from '@maru/protocol'
import { BellOff, Ellipsis, Hash, Headphones, Lock, Pencil } from 'lucide-react'
import { useT } from './i18n.ts'
import { IconButton } from './kit/button.tsx'
import { Avatar, Badge } from './kit/display.tsx'
import { useLongPress } from './kit/long_press.ts'

/** 줄 곁의 … 단추. 메뉴는 그 단추를 자리로 연다(오른쪽 클릭·길게 누르기가 같이 쓴다). */
function more_of(row: HTMLElement): HTMLButtonElement | null {
  return row.parentElement?.querySelector<HTMLButtonElement>('.ChannelRow-more') ?? null
}

export interface ChannelRowProps {
  id: ChannelId
  name: string
  unread: number
  mention_count: number
  selected: boolean
  /** 여기에 쓰다 만 글이 있다. */
  drafting?: boolean
  /** 대화(DM·그룹 DM)다. `#` 대신 상대의 얼굴 글자를 두고, 메뉴 이름도 '대화 옵션' 이다. */
  dm?: boolean
  /** 대화방의 나 아닌 사람들. 한 사람이면 그 사람의 얼굴, 여럿이면 사람 수를 그린다. 모르면 이름 글자. */
  dm_with?: readonly number[] | undefined
  /** 나 혼자의 방이면 나 — 방 이름 글자 대신 내 얼굴. */
  dm_self?: { user: number; name: string } | undefined
  /** 비공개 채널이다. `#` 대신 자물쇠. */
  secret?: boolean
  /** 여기서 허들이 열려 있다. */
  huddle?: boolean
  /** 알림을 껐다(`notify: none`). 배지가 없으니 줄이 흐리고 종 꺼짐이 서야 꺼 둔 줄 안다(3차 F320). */
  muted?: boolean
  /** 칸 머리글로 끌어다 놓을 수 있다. 없으면 끌리지 않는다. */
  draggable?: boolean
  press(id: ChannelId): void
  /** 줄 메뉴를 연다(… 단추를 자리로). 있으면 줄 끝에 … 가 서고 오른쪽 클릭도 같은 메뉴를 연다. */
  more?(id: ChannelId, anchor: HTMLElement): void
  /** 이 줄의 메뉴가 열려 있다. */
  more_open?: boolean
}

/** 끌어 옮기는 채널 번호를 싣는 자리. 다른 끌기(파일 등)와 섞이지 않게 제 이름을 쓴다. */
export const CHANNEL_DRAG = 'application/x-maru-channel'
/** 사이드바 섹션 머리를 끌 때의 자료형(G34). 채널·파일 끌기와 섞이지 않게 따로 둔다. */
export const SECTION_DRAG = 'application/x-maru-section'

/**
 * 사이드바 한 줄.
 *
 * ## 안읽음과 멘션을 다르게 그린다
 * 안읽음은 **굵게**, 멘션은 **숫자 배지**다. 둘을 같은 숫자로 그리면
 * 사람들은 곧 배지를 무시하게 되고, 그러면 진짜 호출을 놓친다.
 */
export function ChannelRow(props: ChannelRowProps) {
  const { id, name, unread, mention_count, selected, drafting, press } = props
  const t = useT()
  const has_unread = unread > 0
  // 대화방은 한 줄 한 줄이 나에게 한 말이라 새 글 수가 곧 부른 수다(Slack 과 같다). 채널처럼 굵게만 두면
  // 방금 온 DM 이 조용한 채널의 안 읽음과 구분되지 않는다(3차 F246). 음소거면 `badge_of` 가 이미 0 을 준다.
  const called = props.dm === true ? Math.max(mention_count, unread) : mention_count
  // 스크린 리더가 줄 하나를 한 문장으로 읽는다 — 그림(배지·연필·헤드폰)이 말하는 것을 다 싣는다.
  const said = [
    name,
    ...(props.secret === true ? [t('비공개 채널')] : []),
    ...(props.dm === true && called > 0
      ? [t('새 메시지 {n}개', { n: called })]
      : mention_count > 0
        ? [t('멘션 {n}개', { n: mention_count })]
        : []),
    ...(has_unread ? [t('읽지 않음')] : []),
    ...(drafting === true ? [t('작성 중인 초안')] : []),
    ...(props.huddle === true ? [t('허들 진행 중')] : []),
    ...(props.muted === true ? [t('알림 꺼짐')] : []),
  ].join(', ')
  // 손가락에는 오른쪽 클릭이 없다 — 길게 누르면 같은 메뉴다. 짧은 누름은 그대로 click(채널 열기)이다.
  const hold = useLongPress<HTMLButtonElement>((row) => {
    const more = more_of(row)
    if (props.more !== undefined && more !== null) props.more(id, more)
  })
  const row = (
    <button
      type="button"
      // 스크린 리더가 "선택됨" 을 읽게 한다. 색만으로 표시하면 안 보이는 사람이 있다.
      aria-current={selected ? 'page' : undefined}
      aria-label={said}
      className={`ChannelRow${selected ? ' 선택됨' : ''}${has_unread ? ' 안읽음' : ''}${props.muted === true ? ' 조용함' : ''}`}
      onClick={() => press(id)}
      {...hold}
      // 오른쪽 클릭은 곁의 … 단추를 대신 누른다 — 메뉴가 둘이면 두 벌을 맞춰야 한다. 길게 누르기가 먼저 막았으면
      // (안드로이드의 contextmenu) 그 메뉴가 이미 떠 있다.
      onContextMenu={(e) => {
        hold.onContextMenu(e)
        if (e.defaultPrevented) return
        const more = more_of(e.currentTarget)
        if (props.more === undefined || more === null) return
        e.preventDefault()
        props.more(id, more)
      }}
      draggable={props.draggable === true}
      {...(props.draggable === true
        ? {
            onDragStart: (e: React.DragEvent) => {
              e.dataTransfer.setData(CHANNEL_DRAG, String(id))
              e.dataTransfer.effectAllowed = 'move'
            },
          }
        : {})}
    >
      {props.dm === true ? (
        <DmFace className="샵" name={name} with={props.dm_with} self={props.dm_self} />
      ) : props.secret === true ? (
        <Lock className="샵" size={14} aria-hidden />
      ) : (
        <Hash className="샵" size={15} aria-hidden />
      )}
      <span className="name">{name}</span>
      {props.huddle === true && (
        <Headphones className="huddle-mark" size={14} aria-hidden>
          <title>{t('허들 진행 중')}</title>
        </Headphones>
      )}
      {props.muted === true && (
        <BellOff className="muted-mark" size={13} aria-hidden>
          <title>{t('알림 꺼짐')}</title>
        </BellOff>
      )}
      {drafting === true && (
        <Pencil className="draft" size={13} aria-hidden>
          <title>{t('작성 중인 초안')}</title>
        </Pencil>
      )}
      <Badge className="멘션배지" tone="danger" count={called} aria-hidden />
    </button>
  )
  if (props.more === undefined) return row
  return (
    <div className="ChannelRow-line">
      {row}
      <IconButton
        className="ChannelRow-more"
        size="sm"
        // 줄마다 같은 이름이면 화면 읽기에서 어느 채널의 메뉴인지 모른다(F237).
        label={
          props.dm === true ? t('{name} 대화 옵션', { name }) : t('{name} 채널 옵션', { name })
        }
        icon={<Ellipsis aria-hidden />}
        aria-haspopup="menu"
        aria-expanded={props.more_open === true}
        onClick={(e) => props.more?.(id, e.currentTarget)}
      />
    </div>
  )
}

/**
 * 대화방의 얼굴(3차 F255). 한 사람과의 방은 **그 사람의 얼굴**(사진·색이 구성원 판과 같다), 여럿의 방은 **사람 수**다.
 * 방 이름("김마루, 이하늘")의 첫 글자를 따면 '김' 이라는 한 사람처럼 보였고, 색도 김마루의 얼굴과 달랐다.
 */
export function DmFace(props: {
  className: string
  name: string
  with?: readonly number[] | undefined
  /** 나 혼자의 방이면 나. */
  self?: { user: number; name: string } | undefined
  size?: 'xs' | 'sm'
}) {
  const w = props.with
  const size = props.size ?? 'xs'
  if (w !== undefined && w.length > 1)
    return (
      <span className={`${props.className} DmCount`} data-size={size} aria-hidden>
        {w.length}
      </span>
    )
  const self = w?.length === 0 ? props.self : undefined
  return (
    <Avatar
      className={props.className}
      size={size}
      name={self?.name || props.name}
      {...(w?.length === 1 ? { user: w[0] } : self !== undefined ? { user: self.user } : {})}
      decorative
    />
  )
}
